/* Section: locations — proximity search, filter and sort.
   --------------------------------------------------------------------------
   Shopify port target: assets/section-locations.js.

   ARCHITECTURE NOTE
   This script never RENDERS a location. Every card already exists in the
   markup — statically here, generated by a Liquid loop over the `location`
   metaobject in Shopify. The script only sorts, hides and annotates what is
   already on the page. Swap the static <li> list for a Liquid loop emitting
   the same classes and data-* attributes and this file works unchanged, and
   the full stockist list stays server-rendered for search engines.

   SEARCH BEHAVIOUR
   - A 5-digit ZIP, or a known "city, ST", resolves to a coordinate and the
     list is SORTED by real distance, nearest first, with each card showing
     how far it is. Nothing is hidden: with ten stockists, ranking is more
     useful than filtering.
   - Anything else is treated as a name filter and hides non-matches.
   - "Use my location" takes the same path as a ZIP, via the browser's own
     geolocation.

   Coordinates for MO and IL ship with the page (every physical stockist is in
   one of those two states). Any other ZIP falls back to the free
   zippopotam.us API, so an out-of-state visitor still gets a sensible answer.
   -------------------------------------------------------------------------- */
(function () {
  /* Scoped to the page, not to one section. Shopify caps a section at 50
     blocks and there are 66 stockists, so the cards are split across three
     sections and the controls live in the first one. */
  var root = document.querySelector('[data-section="locations"]');
  if (!root) return;
  var scope = document;

  var input   = scope.querySelector('.locations__input');
  var form    = scope.querySelector('.locations__form');
  var nearBtn = scope.querySelector('.locations__near');
  var chips   = Array.prototype.slice.call(scope.querySelectorAll('.locations__chip'));
  var countEl = scope.querySelector('.locations__count');
  var nearEl  = scope.querySelector('.locations__near-label');
  var emptyEl = scope.querySelector('.locations__empty');
  var groups  = Array.prototype.slice.call(scope.querySelectorAll('.locations__group'));
  var cards   = Array.prototype.slice.call(scope.querySelectorAll('.locations__card'));
  var mapEl   = scope.querySelector('.locations__map');

  var total = cards.length;
  var activeKind = 'all';
  var origin = null;          // {lat, lng, label} once a place is resolved
  var geoData = null;         // lazily fetched centroid table
  var storeMap = null;
  var markers = {};
  var activeCard = null;

  function markerIcon(active) {
    return window.L.divIcon({
      className: 'locations__marker-wrap',
      html: '<span class="locations__marker' + (active ? ' is-active' : '') + '"><span></span></span>',
      iconSize: active ? [34, 34] : [26, 26],
      iconAnchor: active ? [17, 17] : [13, 13]
    });
  }

  function setActive(card, pan) {
    if (!card || card.hidden) return;
    if (activeCard && activeCard !== card) {
      activeCard.classList.remove('is-map-active');
      var previous = markers[activeCard.getAttribute('data-location-id')];
      if (previous) previous.setIcon(markerIcon(false));
    }
    activeCard = card;
    card.classList.add('is-map-active');
    var marker = markers[card.getAttribute('data-location-id')];
    if (!marker) return;
    marker.setIcon(markerIcon(true));
    marker.setZIndexOffset(1000);
    if (pan && storeMap) storeMap.panTo(marker.getLatLng(), { animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches });
  }

  function clearActive(card) {
    if (!card || activeCard !== card) return;
    card.classList.remove('is-map-active');
    var marker = markers[card.getAttribute('data-location-id')];
    if (marker) {
      marker.setIcon(markerIcon(false));
      marker.setZIndexOffset(0);
    }
    activeCard = null;
  }

  function initMap() {
    if (!mapEl || !window.L) return;
    storeMap = window.L.map(mapEl, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(storeMap);

    var bounds = [];
    cards.forEach(function (card) {
      if (card.__lat === null || card.__lng === null || card.__kind === 'online') return;
      var id = card.getAttribute('data-location-id');
      var marker = window.L.marker([card.__lat, card.__lng], {
        icon: markerIcon(false),
        keyboard: true,
        riseOnHover: true,
        title: card.querySelector('.locations__name').textContent.trim(),
        alt: 'Show ' + card.querySelector('.locations__name').textContent.trim()
      }).addTo(storeMap);
      markers[id] = marker;
      bounds.push([card.__lat, card.__lng]);
      marker.on('mouseover focus', function () { setActive(card, false); });
      marker.on('mouseout blur', function () { clearActive(card); });
      marker.on('click', function () {
        setActive(card, true);
        card.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        card.focus({ preventScroll: true });
      });
      card.addEventListener('mouseenter', function () { setActive(card, true); });
      card.addEventListener('mouseleave', function () { clearActive(card); });
      card.addEventListener('focusin', function () { setActive(card, true); });
      card.addEventListener('focusout', function () { clearActive(card); });
    });
    if (bounds.length) storeMap.fitBounds(bounds, { padding: [28, 28], maxZoom: 10 });
    window.setTimeout(function () { storeMap.invalidateSize(); }, 0);
  }

  /* Shopify splits the directory across sections when the block count is
     larger than the section limit. Once JavaScript is available, gather those
     server-rendered groups into one scrollable results pane beside the map. */
  function buildDirectoryLayout() {
    if (!mapEl) return;
    var firstWrap = root.querySelector('.wrap');
    var controls = root.querySelector('.locations__controls');
    var mapShell = root.querySelector('.locations__map-shell');
    if (!firstWrap || !mapShell) return;

    var layout = document.createElement('div');
    layout.className = 'locations__directory-layout';

    var results = document.createElement('div');
    results.className = 'locations__results';
    results.id = 'locations-results';
    results.setAttribute('aria-label', 'Store listings');

    var mapPane = document.createElement('div');
    mapPane.className = 'locations__map-pane';

    firstWrap.appendChild(layout);
    layout.appendChild(results);
    layout.appendChild(mapPane);
    mapPane.appendChild(mapShell);

    groups.forEach(function (group) { results.appendChild(group); });

    /* The search bar sticks below the masthead, and the map has to stick below
       BOTH or its top edge — which carries the "Find Freddie Lee's near you"
       panel — slides underneath the search bar and is never seen. The bar's
       height changes with viewport width (the field, the chips and the count
       rewrap), so it is measured rather than guessed and republished as a
       custom property the stylesheet offsets against. */
    if (controls) {
      var publishControlsHeight = function () {
        root.style.setProperty('--fl-controls-h', controls.offsetHeight + 'px');
      };
      publishControlsHeight();
      if ('ResizeObserver' in window) {
        new ResizeObserver(publishControlsHeight).observe(controls);
      } else {
        window.addEventListener('resize', publishControlsHeight);
      }
    }

    var emptyStates = Array.prototype.slice.call(scope.querySelectorAll('.locations__empty'));
    emptyStates.forEach(function (empty, index) {
      if (index === 0) results.appendChild(empty);
      else empty.remove();
    });

    Array.prototype.slice.call(scope.querySelectorAll('[data-section="locations"]')).forEach(function (section) {
      if (section !== root) section.hidden = true;
    });

    if (controls) controls.setAttribute('aria-controls', results.id);
  }

  cards.forEach(function (el) {
    el.__hay  = el.textContent.toLowerCase().replace(/\s+/g, ' ');
    el.__kind = el.getAttribute('data-kind') || '';
    var lat = parseFloat(el.getAttribute('data-lat'));
    var lng = parseFloat(el.getAttribute('data-lng'));
    el.__lat = isNaN(lat) ? null : lat;
    el.__lng = isNaN(lng) ? null : lng;
  });

  buildDirectoryLayout();

  /* --- geo helpers ------------------------------------------------------- */

  function haversine(aLat, aLng, bLat, bLng) {
    var R = 3958.8; // miles
    var dLat = (bLat - aLat) * Math.PI / 180;
    var dLng = (bLng - aLng) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function loadGeo() {
    if (geoData) return Promise.resolve(geoData);
    return Promise.resolve(window.FREDDIE_ZIPS)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { geoData = j; return j; })
      .catch(function () { return null; });
  }

  /* Resolve free text to a coordinate. Local table first, then the ZIP API. */
  function resolvePlace(q) {
    var zip = q.match(/^\s*(\d{5})(?:-\d{4})?\s*$/);
    var key = q.trim().toLowerCase().replace(/\s+/g, ' ');

    return loadGeo().then(function (g) {
      if (g) {
        if (zip && g.zips[zip[1]]) {
          return { lat: g.zips[zip[1]][0], lng: g.zips[zip[1]][1], label: zip[1] };
        }
        if (!zip && g.cities[key]) {
          return { lat: g.cities[key][0], lng: g.cities[key][1], label: titleCase(key) };
        }
      }
      if (!zip) return null;                     // not a ZIP, not a known city
      // Out-of-state ZIP: ask the free ZIP API rather than shipping 42,000 rows.
      return fetch('https://api.zippopotam.us/us/' + zip[1])
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (!j || !j.places || !j.places.length) return null;
          return {
            lat: parseFloat(j.places[0].latitude),
            lng: parseFloat(j.places[0].longitude),
            label: zip[1]
          };
        })
        .catch(function () { return null; });
    });
  }

  function titleCase(s) {
    return s.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); })
            .replace(/,\s*([a-z]{2})$/i, function (m, st) { return ', ' + st.toUpperCase(); });
  }

  /* --- rendering --------------------------------------------------------- */

  function setNearLabel(text) {
    if (!nearEl) return;
    nearEl.textContent = text || '';
    nearEl.hidden = !text;
  }

  // "listed by name" rather than a bare count. The brand also says 1,200+ stores
  // nationwide, and this page can only name the ones on the client's own master
  // list. Reading "200 places to buy" directly under "1,200+ stores" invites a
  // subtraction that makes one of the two numbers look like a lie, when in fact
  // they are counting different things: doors we can address, against reach.
  function label(shown) {
    if (!countEl) return;
    countEl.textContent = shown === total
      ? total + ' stores listed by name'
      : shown + ' of ' + total + ' stores listed by name';
  }

  function applyDistances() {
    cards.forEach(function (el) {
      var slot = el.querySelector('.locations__dist');
      if (!origin || el.__lat === null) {
        el.__dist = Infinity;
        if (slot) slot.textContent = '';
        return;
      }
      el.__dist = haversine(origin.lat, origin.lng, el.__lat, el.__lng);
      if (slot) slot.textContent = el.__dist.toFixed(1) + ' mi away';
    });

    if (!origin) return;
    /* Reorder inside each group so the headings stay truthful. Cards with no
       coordinates (the chains) keep their order and sort to the end. */
    groups.forEach(function (g) {
      var list = g.querySelector('.locations__list');
      if (!list) return;
      Array.prototype.slice.call(list.querySelectorAll('.locations__card'))
        .sort(function (a, b) { return a.__dist - b.__dist; })
        .forEach(function (el) { list.appendChild(el); });
    });
    root.setAttribute('data-sorted', 'distance');
  }

  function apply(textQuery) {
    var q = (textQuery || '').trim().toLowerCase();
    var shown = 0;

    cards.forEach(function (el) {
      var textHit = !q || el.__hay.indexOf(q) !== -1;
      var kindHit = activeKind === 'all' || el.__kind === activeKind;
      var hit = textHit && kindHit;
      el.hidden = !hit;
      if (hit) shown++;
    });

    groups.forEach(function (g) {
      g.hidden = g.querySelectorAll('.locations__card:not([hidden])').length === 0;
    });

    if (emptyEl) emptyEl.hidden = shown !== 0;
    label(shown);
  }

  /* --- search ------------------------------------------------------------ */

  var searching = false;

  function runSearch(raw) {
    var q = (raw || '').trim();

    if (!q) {                       // cleared: drop proximity, show everything
      origin = null;
      setNearLabel('');
      root.removeAttribute('data-sorted');
      applyDistances();
      apply('');
      return;
    }

    var looksGeographic = /^\s*\d{5}(-\d{4})?\s*$/.test(q) || /,\s*[a-z]{2}\s*$/i.test(q);
    if (!looksGeographic) {         // treat as a store-name filter
      origin = null;
      setNearLabel('');
      applyDistances();
      apply(q);
      return;
    }

    if (searching) return;
    searching = true;
    setNearLabel('Searching…');

    resolvePlace(q).then(function (place) {
      searching = false;
      if (!place) {
        origin = null;
        setNearLabel('');
        apply(q);                   // fall back to text matching
        return;
      }
      origin = place;
      applyDistances();
      apply('');                    // proximity ranks, it does not hide
      setNearLabel('near ' + place.label);
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      runSearch(input.value);
      input.blur();                 // dismiss the phone keyboard
    });
  }

  if (input) {
    var t;
    input.addEventListener('input', function () {
      clearTimeout(t);
      var v = input.value;
      /* Only live-filter free text. A part-typed ZIP ("630") should not fire a
         lookup on every keystroke — those wait for submit. */
      if (/^\s*\d/.test(v) && v.trim().length < 5) return;
      t = setTimeout(function () { runSearch(v); }, 220);
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.value) { input.value = ''; runSearch(''); }
    });
  }

  chips.forEach(function (chip) {
    chip.addEventListener('click', function () {
      activeKind = chip.getAttribute('data-kind') || 'all';
      chips.forEach(function (c) { c.setAttribute('aria-pressed', String(c === chip)); });
      apply(origin ? '' : (input ? input.value : ''));
    });
  });

  /* --- use my location --------------------------------------------------- */

  if (nearBtn) {
    if (!('geolocation' in navigator)) {
      nearBtn.hidden = true;
    } else {
      nearBtn.addEventListener('click', function () {
        var original = nearBtn.textContent;
        nearBtn.disabled = true;
        nearBtn.textContent = 'Locating…';

        navigator.geolocation.getCurrentPosition(function (pos) {
          origin = { lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'you' };
          if (input) input.value = '';
          applyDistances();
          apply('');
          setNearLabel('near you');
          nearBtn.disabled = false;
          nearBtn.textContent = original;
        }, function () {
          nearBtn.disabled = false;
          nearBtn.textContent = original;
          setNearLabel('Location unavailable. Search by ZIP instead.');
        }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
      });
    }
  }

  apply('');
  initMap();
})();
